import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import {
  CreateEditInstructionsModel,
  OpenInstructionModel
} from './create-edit-instructions/create-edit-instructions.model';
import { InstructionNotificationModel } from '../shared/components/instruction-notification/instruction-notification-model';
import { JsonApiWrapper } from '../shared/types/jsonApiWrapper.types';
import { jsonApiTypes } from '../shared/constants';
import {
  IAipInstructionSearchResponse,
  ICreateUpdateInstructionResponse,
  IValidateInstructionResponse
} from './mutual-fund-investing.model';
import { IAipInstructionSearchModel } from './view-instructions/view-instructions.model';
import { AccountRecord } from '../shared/components/account-search/account-search.model';
import { AppSettingsService } from '@charlesschwab/as-ui-core';
import { ComponentDataShareService } from '../shared/services/component-data-share.service';

class CreateEditInstructionsViewMode {
  public static readonly CREATE_INSTRUCTION = 'CREATE';
  public static readonly EDIT_INSTRUCTION = 'EDIT';
  public static readonly PREVIEW_INSTRUCTION = 'PREVIEW';
  public static readonly VIEW_INSTRUCTION = 'VIEW';
  public static readonly SUBMITTED_INSTRUCTION = 'SUBMITTED';
}

@Injectable({
  providedIn: 'root'
})
class MutualFundInvestingService {
  private createEditModalSource: Subject<any> = new Subject();
  public createEditModal$ = this.createEditModalSource.asObservable();

  private existingInstructionSource: Subject<any> = new Subject();
  public existingInstruction$ = this.existingInstructionSource.asObservable();

  private instructionNotificationSource: Subject<any> = new Subject();
  public instructionNotification$ = this.instructionNotificationSource.asObservable();

  private clearHardEditNotificationSource: Subject<any> = new Subject();
  public clearHardEditNotification$ = this.clearHardEditNotificationSource.asObservable();

  private clearInstructionAlreadyExistsNotificationSource: Subject<any> = new Subject();
  public clearInstructionAlreadyExistsNotification$ = this.clearInstructionAlreadyExistsNotificationSource.asObservable();

  private stopInstructionSource: Subject<any> = new Subject();
  public stopInstruction$ = this.stopInstructionSource.asObservable();

  private deleteInstructionSource: Subject<any> = new Subject();
  public deleteInstruction$ = this.deleteInstructionSource.asObservable();

  private updateInstructionStatusSource: Subject<any> = new Subject();
  public updateInstructionStatus$ = this.updateInstructionStatusSource.asObservable();

  private refreshMutualFundInstructionsSource: Subject<void> = new Subject();
  public refreshMutualFundInstructions$ = this.refreshMutualFundInstructionsSource.asObservable();

  private masterAccountListSource: Subject<any> = new Subject();
  public masterAccountList$ = this.masterAccountListSource.asObservable();
  Response: string;
  errorMessage: boolean;
  constructor(
    private http: HttpClient,
    private settingsService: AppSettingsService,
    private componentDataShareService: ComponentDataShareService
  ) {}

  getMutualFundInstructions(aipInstructionsSearchRequest: IAipInstructionSearchModel): Observable<any> {
    const url = `${this.settingsService.settings.bffGatewayUrl}/api/ria.SacAipExperience/v1/AipInstructions`;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/vnd.api+json',
        Accept: 'application/vnd.api+json'
      })
    };
    this.errorMessage = false;
    this.componentDataShareService.setMessage(this.errorMessage);
    const jsonApiInstructionSearch = new JsonApiWrapper(
      aipInstructionsSearchRequest,
      jsonApiTypes.aipInstructionSearchRequest
    );
    return this.http.post<IAipInstructionSearchResponse>(url, jsonApiInstructionSearch, httpOptions).pipe(
      map((response: IAipInstructionSearchResponse) => response),
      timeout(10000),
      catchError((err) => {
        return this.handleError(err);
      })
    );
  }
  private handleError(error: Response | any): Observable<never> {
    let errMsg: string;
    if (error instanceof Response) {
      const body = error.json() || '';
      const err = body || JSON.stringify(body);
      errMsg = `${error.status} - ${error.statusText || ''} ${err}`;
    } else {
      errMsg = error.message ? error.message : error.toString();
    }
    this.errorMessage = true;
    this.componentDataShareService.setMessage(this.errorMessage);
    return throwError(() => new Error(errMsg));
  }

  openCreateEditInstructions = (instruction?: OpenInstructionModel) => {
    this.createEditModalSource.next(instruction);
  };

  openExistingInstructionPopup = (instruction?: any) => {
    this.existingInstructionSource.next(instruction);
  };

  createUpdateInstruction(instructionData: CreateEditInstructionsModel): Observable<any> {
    const url = `${this.settingsService.settings.bffGatewayUrl}/api/ria.SacAipExperience/v1/CreateInstruction`;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/vnd.api+json',
        Accept: 'application/vnd.api+json'
      })
    };

    const jsonApiInstructionData = new JsonApiWrapper(instructionData, jsonApiTypes.aipInstructionRequest);

    return this.http
      .post<ICreateUpdateInstructionResponse>(url, jsonApiInstructionData, httpOptions)
      .pipe(map((response: ICreateUpdateInstructionResponse) => response));
  }

  showHideInstructionNotification(notficationData?: InstructionNotificationModel): void {
    this.instructionNotificationSource.next(notficationData);
  }

  clearHardEditNotification(notficationData?: InstructionNotificationModel): void {
    this.clearHardEditNotificationSource.next(notficationData);
  }

  clearInstructionAlreadyExistsNotification(notficationData?: InstructionNotificationModel): void {
    this.clearInstructionAlreadyExistsNotificationSource.next(notficationData);
  }

  stopInstruction(instruction: CreateEditInstructionsModel): void {
    this.stopInstructionSource.next(instruction);
  }

  deleteInstruction(instruction: CreateEditInstructionsModel): void {
    this.deleteInstructionSource.next(instruction);
  }

  updateInstructionStatus(instruction: CreateEditInstructionsModel): void {
    this.updateInstructionStatusSource.next(instruction);
  }

  refreshMutualFundInstructions(): void {
    this.refreshMutualFundInstructionsSource.next();
  }

  validateInstruction(instructionData: CreateEditInstructionsModel): Observable<any> {
    const url = `${this.settingsService.settings.bffGatewayUrl}/api/ria.SacAipExperience/v1/ValidateInstruction`;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/vnd.api+json',
        Accept: 'application/vnd.api+json'
      })
    };

    const jsonApiValidateInstructionData = new JsonApiWrapper(instructionData, jsonApiTypes.aipInstructionRequest);

    return this.http
      .post<IValidateInstructionResponse>(url, jsonApiValidateInstructionData, httpOptions)
      .pipe(map((response: IValidateInstructionResponse) => response));
  }

  masterAccountListUpdated(list: Array<AccountRecord>): void {
    this.masterAccountListSource.next(list);
  }
}
export { MutualFundInvestingService, CreateEditInstructionsViewMode };
